/**
 * Dependencies
 */
import { cloneDeep } from 'lodash'

/**
 * Base State
 *
 * @type {Object}
 */
const base = {
    capex_loading: false,
    loaded: false,
    pending: false,
    dirty: false,
    success: false,
    data: {},
    options: {},
    errors: {},
    tabs: [],
    draft: {},
    meta: {},
    saving_draft: false,
    activeTab: 'project',
    attemptingSave: false,
    projectChoices: {
        selected: {
            primary: null,
            secondary: null
        },
        options: {
            residential: {
                title: 'Residential',
                key: 'residential',
                options: {
                    single_family: {
                        title: 'Single Family',
                        key: 'single_family',
                        options: {
                            spec_house: {
                                title: 'Spec House',
                                form_type: 'R_SF_spec_house',
                                selected: false
                            },
                            fix_and_flip: {
                                title: 'Fix and Flip',
                                form_type: 'R_SF_fix_and_flip',
                                selected: false
                            },
                            buy_and_hold: {
                                title: 'Buy and Hold',
                                form_type: 'R_SF_buy_and_hold',
                                selected: false
                            }
                        }
                    },
                    multi_family: {
                        title: 'Multi Family',
                        key: 'multi_family',
                        options: {
                            apartment_development: {
                                title: 'Apartment Development',
                                form_type: 'R_MF_apartment_development',
                                disabled: false
                            },
                            condo_development: {
                                title: 'Condo Development',
                                form_type: 'R_MF_condo_development',
                                disabled: false
                            },
                            value_add: {
                                title: 'Value Add',
                                form_type: 'R_MF_value_add',
                                disabled: process.env.NODE_ENV === 'production'
                            },
                            buy_hold: {
                                title: 'Buy and Hold',
                                form_type: 'R_MF_buy_and_hold',
                                disabled: false
                            }
                        }
                    },
                    student_housing: {
                        title: 'Student Housing',
                        key: 'student_housing',
                        options: {
                            development: {
                                title: 'Development',
                                form_type: 'R_SH_development',
                                selected: false
                            },
                            buy_and_hold: {
                                title: 'Buy and Hold',
                                form_type: 'R_SH_buy_and_hold',
                                selected: false
                            },
                            value_add: {
                                title: 'Value Add',
                                form_type: 'R_SH_value_add',
                                selected: false,
                                disabled: true
                            }
                        }
                    }
                }
            },
            commercial: {
                title: 'Commercial',
                key: 'commercial',
                options: {
                    office: {
                        title: 'Office',
                        key: 'office',
                        disabled: true,
                        options: {
                            development: {
                                title: 'Development',
                                form_type: 'C_O_development',
                                selected: false
                            },
                            value_add: {
                                title: 'Value Add',
                                form_type: 'C_O_value_add',
                                selected: false
                            },
                            buy_hold: {
                                title: 'Buy Hold',
                                form_type: 'C_O_buy_and_hold',
                                selected: false
                            },
                            land_development: {
                                title: 'Land Development',
                                form_type: 'C_O_land_development',
                                selected: false
                            }
                        }
                    },
                    industrial: {
                        title: 'Industrial',
                        key: 'industrial',
                        disabled: true,
                        options: {
                            development: {
                                title: 'Development',
                                form_type: 'C_I_development',
                                selected: false
                            },
                            value_add: {
                                title: 'Value Add',
                                form_type: 'C_I_value_add',
                                selected: false
                            },
                            buy_hold: {
                                title: 'Buy Hold',
                                form_type: 'C_I_buy_and_hold',
                                selected: false
                            }
                        }
                    },
                    retail: {
                        title: 'Retail',
                        key: 'retail',
                        disabled: true,
                        options: {
                            development: {
                                title: 'Development',
                                form_type: 'C_R_development',
                                selected: false
                            },
                            value_add: {
                                title: 'Value Add',
                                form_type: 'C_R_value_add',
                                selected: false
                            },
                            buy_hold: {
                                title: 'Buy Hold',
                                form_type: 'C_R_buy_and_hold',
                                selected: false
                            }
                        }
                    },
                    medical: {
                        title: 'Medical',
                        key: 'medical',
                        disabled: true,
                        options: {
                            development: {
                                title: 'Development',
                                form_type: 'C_M_development',
                                selected: false
                            },
                            value_add: {
                                title: 'Value Add',
                                form_type: 'C_M_value_add',
                                selected: false
                            },
                            buy_hold: {
                                title: 'Buy Hold',
                                form_type: 'C_M_buy_and_hold',
                                selected: false
                            }
                        }
                    },
                    hospitality: {
                        title: 'Hospitality',
                        key: 'hospitality',
                        disabled: true,
                        options: {
                            development: {
                                title: 'Development',
                                form_type: 'C_H_development',
                                selected: false
                            },
                            value_add: {
                                title: 'Value Add',
                                form_type: 'C_H_value_add',
                                selected: false
                            },
                            buy_hold: {
                                title: 'Buy Hold',
                                form_type: 'C_H_buy_and_hold',
                                selected: false
                            }
                        }
                    },
                    mixed_use: {
                        title: 'Mixed Use',
                        key: 'mixed_use',
                        disabled: true,
                        options: {
                            development: {
                                title: 'Development',
                                form_type: 'C_MU_development',
                                selected: false
                            },
                            value_add: {
                                title: 'Value Add',
                                form_type: 'C_MU_value_add',
                                selected: false
                            },
                            buy_hold: {
                                title: 'Buy Hold',
                                form_type: 'C_MU_buy_and_hold',
                                selected: false
                            }
                        }
                    }
                }
            },
            land: {
                title: 'Land / Natural Resources',
                key: 'land',
                disabled: true,
                options: {}
            }
        }
    }
}

/**
 * Return a fresh copy of the base state
 *
 * @return {Object}
 */
export const getState = () => cloneDeep(base)

export default getState()



// WEBPACK FOOTER //
// ./src/state/modules/projects/form/state.js